import EmptyState from '@/components/common/EmptyState';
import Pagination from '@/components/common/Pagination';
import SelectInput from '@/components/Forms/SelectInput';
import TextInput from '@/components/Forms/TextInput';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import AppLayout from '@/layouts/AppLayout';
import { Download, FileText } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Resource {
    id: number;
    title: string;
    category: string;
    file_url: string | null;
}

interface Props {
    recursos: Resource[];
}

const ALL_CATEGORIES = 'all';
const PAGE_SIZE = 24;

const isPdf = (url: string | null): boolean =>
    (url ?? '').toLowerCase().split('?')[0].endsWith('.pdf');

const RecursosContent: React.FC<Props> = ({ recursos }) => {
    const { t } = useTranslation();
    const [categoryFilter, setCategoryFilter] =
        useState<string>(ALL_CATEGORIES);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);

    const categories = useMemo(() => {
        const seen = new Set<string>();
        recursos.forEach((r) => {
            if (r.category) {
                seen.add(r.category);
            }
        });
        return Array.from(seen);
    }, [recursos]);

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        return recursos.filter((r) => {
            const matchesCategory =
                categoryFilter === ALL_CATEGORIES ||
                r.category === categoryFilter;
            const matchesSearch =
                term === '' || r.title.toLowerCase().includes(term);
            return matchesCategory && matchesSearch;
        });
    }, [recursos, categoryFilter, search]);

    useEffect(() => {
        setPage(1);
    }, [categoryFilter, search]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paged = useMemo(
        () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        [filtered, page]
    );

    const hasResources = recursos.length > 0;

    return (
        <AppLayout>
            <PageTitle title="Recursos" />
            <div className="mx-auto max-w-7xl space-y-8">
                {/* Header */}
                <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700 dark:bg-neutral-800">
                    <h1 className="mb-4 text-center text-3xl font-bold text-gray-900 dark:text-white">
                        📚 {t('recursos.title')}
                    </h1>
                    <p className="text-center text-gray-700 dark:text-neutral-300">
                        {t('recursos.intro')}
                    </p>
                </div>

                {/* Filters */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <SelectInput
                        label={`🗂️ ${t('recursos.filterCategory')}`}
                        name="category_filter"
                        value={categoryFilter}
                        onChange={(v) => setCategoryFilter(v)}
                    >
                        <option value={ALL_CATEGORIES}>
                            {t('recursos.allCategories')}
                        </option>
                        {categories.map((c) => (
                            <option key={c} value={c}>
                                {c}
                            </option>
                        ))}
                    </SelectInput>
                    <TextInput
                        label={`🔎 ${t('recursos.searchLabel')}`}
                        name="search"
                        value={search}
                        onChange={(v) => setSearch(v)}
                        placeholder={t('recursos.searchPlaceholder')}
                    />
                </div>

                {/* Resources grid */}
                {hasResources ? (
                    filtered.length > 0 ? (
                        <div className="space-y-6">
                            <div className="text-sm text-gray-600 dark:text-neutral-400">
                                {t('recursos.showing', {
                                    shown: paged.length,
                                    total: filtered.length,
                                })}
                            </div>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                {paged.map((resource) => (
                                    <a
                                        key={resource.id}
                                        href={resource.file_url ?? '#'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="group flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md dark:border-neutral-700 dark:bg-neutral-800"
                                    >
                                        <div className="flex items-start gap-3">
                                            <span className="bg-theme-lightest text-theme-primary rounded-lg p-2">
                                                <FileText className="h-5 w-5" />
                                            </span>
                                            <div className="min-w-0 flex-1">
                                                <div className="line-clamp-2 font-semibold text-gray-900 dark:text-white">
                                                    {resource.title}
                                                </div>
                                                <div className="mt-1 text-xs text-gray-500 dark:text-neutral-400">
                                                    {resource.category}
                                                </div>
                                            </div>
                                        </div>
                                        <span className="text-theme-primary mt-auto inline-flex items-center gap-1.5 text-sm font-semibold">
                                            <Download className="h-4 w-4" />
                                            {isPdf(resource.file_url)
                                                ? t('recursos.open')
                                                : t('recursos.download')}
                                        </span>
                                    </a>
                                ))}
                            </div>
                            <Pagination
                                variant="simple"
                                currentPage={page}
                                totalPages={totalPages}
                                hasPrevious={page > 1}
                                hasNext={page < totalPages}
                                onPrevious={() =>
                                    setPage((p) => Math.max(1, p - 1))
                                }
                                onNext={() =>
                                    setPage((p) => Math.min(totalPages, p + 1))
                                }
                            />
                        </div>
                    ) : (
                        <EmptyState description={t('recursos.noResults')} />
                    )
                ) : (
                    <EmptyState
                        icon={FileText}
                        description={t('recursos.empty')}
                    />
                )}
            </div>
        </AppLayout>
    );
};

const Recursos: React.FC<Props> = ({ recursos }) => (
    <ThemeWrapper>
        <RecursosContent recursos={recursos} />
    </ThemeWrapper>
);

export default Recursos;
